import React, { useState } from 'react';
import { ArrowLeft, Mail, Lock, Eye, EyeOff, Briefcase, Heart, CheckCircle, Users, Star, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

type UserType = 'professional' | 'regular' | null;

const Login = () => {
  const navigate = useNavigate();
  const { signIn, signUp, loading } = useAuth();
  const [step, setStep] = useState<'select' | 'login' | 'register'>('select');
  const [userType, setUserType] = useState<UserType>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    businessName: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const handleUserTypeSelect = (type: UserType) => {
    setUserType(type);
    setStep('login');
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.email.trim()) {
      errors.email = 'E-mailadres is verplicht';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Ongeldig e-mailadres';
    }

    if (!formData.password) {
      errors.password = 'Wachtwoord is verplicht';
    } else if (formData.password.length < 6) {
      errors.password = 'Wachtwoord moet minimaal 6 karakters zijn';
    }

    return errors;
  };

  const validateRegisterForm = () => {
    const errors = validateForm();

    if (!formData.name.trim()) {
      errors.name = 'Naam is verplicht';
    }

    if (userType === 'professional' && !formData.businessName.trim()) {
      errors.businessName = 'Bedrijfsnaam is verplicht voor professionals';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Wachtwoorden komen niet overeen';
    }

    return errors;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      await signIn(formData.email, formData.password);
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      // Error message is already shown by useAuth
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = validateRegisterForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      await signUp(formData.email, formData.password, {
        user_type: userType!,
        display_name: formData.name,
        business_name: userType === 'professional' ? formData.businessName : undefined,
      });

      // Don't navigate immediately - user needs to verify email
      setStep('login');
      setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
    } catch (error) {
      console.error('Registration error:', error);
      // Error message is already shown by useAuth
    }
  };

  const renderUserTypeSelection = () => (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="script-text text-5xl text-navy-800 mb-4">
          Welkom bij WOM Plaza
        </h1>
        <p className="text-lg text-navy-600 mb-8">
          Kies je account type om te beginnen
        </p>
      </div>

      {/* User Type Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Professional User */}
        <Card 
          className="card-premium border-2 border-cream-300 hover:border-navy-400 cursor-pointer transition-all duration-300 hover:shadow-elegant-lg hover:scale-105"
          onClick={() => handleUserTypeSelect('professional')}
        >
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 bg-navy-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Briefcase className="h-8 w-8 text-navy-600" />
            </div>
            <CardTitle className="text-2xl text-navy-800">
              Professional
            </CardTitle>
            <p className="text-navy-600">
              Voor ondernemers & dienstverleners
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-navy-700">Diensten aanbieden</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-navy-700">Klanten beheren</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-navy-700">Inkomsten bijhouden</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-navy-700">Bedrijfsprofiel</span>
              </div>
            </div>
            <div className="pt-4 border-t border-cream-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-navy-600">Voordelen:</span>
                <Badge variant="outline" className="bg-gold-50 text-gold-700 border-gold-200">
                  Premium Features
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Regular User */}
        <Card 
          className="card-premium border-2 border-cream-300 hover:border-navy-400 cursor-pointer transition-all duration-300 hover:shadow-elegant-lg hover:scale-105"
          onClick={() => handleUserTypeSelect('regular')}
        >
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 bg-cream-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="h-8 w-8 text-navy-600" />
            </div>
            <CardTitle className="text-2xl text-navy-800">
              Gebruiker
            </CardTitle>
            <p className="text-navy-600">
              Voor klanten & service zoekers
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-navy-700">Diensten zoeken & boeken</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-navy-700">Berichten versturen</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-navy-700">Reviews schrijven</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-navy-700">Favorieten bijhouden</span>
              </div>
            </div>
            <div className="pt-4 border-t border-cream-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-navy-600">Voordelen:</span>
                <Badge variant="outline" className="bg-cream-50 text-navy-700 border-cream-200">
                  Gratis Account
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 pt-8 border-t border-cream-200">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Users className="h-5 w-5 text-navy-600" />
            <span className="text-2xl font-bold text-navy-800">2.500+</span>
          </div>
          <p className="text-sm text-navy-600">Actieve gebruikers</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Star className="h-5 w-5 text-gold-500" />
            <span className="text-2xl font-bold text-navy-800">4.8</span>
          </div>
          <p className="text-sm text-navy-600">Gemiddelde rating</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <TrendingUp className="h-5 w-5 text-green-600" />
            <span className="text-2xl font-bold text-navy-800">1.200+</span>
          </div>
          <p className="text-sm text-navy-600">Diensten beschikbaar</p>
        </div>
      </div>
    </div>
  );

  const renderLoginForm = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-navy-100 rounded-full flex items-center justify-center mx-auto mb-4">
          {userType === 'professional' ? (
            <Briefcase className="h-8 w-8 text-navy-600" />
          ) : (
            <Heart className="h-8 w-8 text-navy-600" />
          )}
        </div>
        <h1 className="text-3xl font-serif text-navy-800 mb-2">
          Welkom terug!
        </h1>
        <p className="text-navy-600">
          Log in als {userType === 'professional' ? 'professional' : 'gebruiker'}
        </p>
        <Badge 
          variant="outline" 
          className={`mt-2 ${userType === 'professional' ? 'bg-gold-50 text-gold-700 border-gold-200' : 'bg-cream-50 text-navy-700 border-cream-200'}`}
        >
          {userType === 'professional' ? 'Professional Account' : 'Gebruiker Account'}
        </Badge>
      </div>

      {/* Login Form */}
      <Card className="card-premium">
        <CardContent className="p-6">
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-navy-700">
                E-mailadres
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-navy-400" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="jouw@email.com"
                  className="pl-10 border-cream-300 focus:border-navy-400"
                />
              </div>
              {formErrors.email && (
                <p className="text-red-600 text-sm">{formErrors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-navy-700">
                Wachtwoord
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-navy-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 border-cream-300 focus:border-navy-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-navy-400 hover:text-navy-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {formErrors.password && (
                <p className="text-red-600 text-sm">{formErrors.password}</p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full bg-navy-700 hover:bg-navy-800"
              disabled={loading}
            >
              {loading ? 'Inloggen...' : 'Inloggen'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-navy-600">
              Nog geen account?{' '}
              <button
                type="button"
                onClick={() => setStep('register')}
                className="text-navy-700 font-semibold hover:text-navy-800 underline"
              >
                Registreer hier
              </button>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Change User Type */}
      <div className="text-center">
        <button
          type="button"
          onClick={() => setStep('select')}
          className="text-sm text-navy-500 hover:text-navy-700 underline"
        >
          ← Ander account type kiezen
        </button>
      </div>
    </div>
  );

  const renderRegisterForm = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-navy-100 rounded-full flex items-center justify-center mx-auto mb-4">
          {userType === 'professional' ? (
            <Briefcase className="h-8 w-8 text-navy-600" />
          ) : (
            <Heart className="h-8 w-8 text-navy-600" />
          )}
        </div>
        <h1 className="text-3xl font-serif text-navy-800 mb-2">
          Account aanmaken
        </h1>
        <p className="text-navy-600">
          Registreer als {userType === 'professional' ? 'professional' : 'gebruiker'}
        </p>
        <Badge 
          variant="outline" 
          className={`mt-2 ${userType === 'professional' ? 'bg-gold-50 text-gold-700 border-gold-200' : 'bg-cream-50 text-navy-700 border-cream-200'}`}
        >
          {userType === 'professional' ? 'Professional Account' : 'Gebruiker Account'}
        </Badge>
      </div>

      {/* Register Form */}
      <Card className="card-premium">
        <CardContent className="p-6">
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-navy-700">
                Volledige naam *
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Jouw naam"
                className="border-cream-300 focus:border-navy-400"
              />
              {formErrors.name && (
                <p className="text-red-600 text-sm">{formErrors.name}</p>
              )}
            </div>

            {userType === 'professional' && (
              <div className="space-y-2">
                <Label htmlFor="businessName" className="text-navy-700">
                  Bedrijfsnaam *
                </Label>
                <Input
                  id="businessName"
                  value={formData.businessName}
                  onChange={(e) => handleInputChange('businessName', e.target.value)}
                  placeholder="Jouw bedrijf"
                  className="border-cream-300 focus:border-navy-400"
                />
                {formErrors.businessName && (
                  <p className="text-red-600 text-sm">{formErrors.businessName}</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-navy-700">
                E-mailadres *
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-navy-400" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="jouw@email.com"
                  className="pl-10 border-cream-300 focus:border-navy-400"
                />
              </div>
              {formErrors.email && (
                <p className="text-red-600 text-sm">{formErrors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-navy-700">
                Wachtwoord *
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-navy-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 border-cream-300 focus:border-navy-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-navy-400 hover:text-navy-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {formErrors.password && (
                <p className="text-red-600 text-sm">{formErrors.password}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-navy-700">
                Bevestig wachtwoord *
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-navy-400" />
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 border-cream-300 focus:border-navy-400"
                />
              </div>
              {formErrors.confirmPassword && (
                <p className="text-red-600 text-sm">{formErrors.confirmPassword}</p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full bg-navy-700 hover:bg-navy-800"
              disabled={loading}
            >
              {loading ? 'Account aanmaken...' : 'Account aanmaken'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-navy-600">
              Heb je al een account?{' '}
              <button
                type="button"
                onClick={() => setStep('login')}
                className="text-navy-700 font-semibold hover:text-navy-800 underline"
              >
                Log hier in
              </button>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Change User Type */}
      <div className="text-center">
        <button
          type="button"
          onClick={() => setStep('select')}
          className="text-sm text-navy-500 hover:text-navy-700 underline"
        >
          ← Ander account type kiezen
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-elegant">
      {/* Header */}
      <header className="px-6 py-6">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/')}
          className="p-2 hover:bg-cream-100 transition-all duration-300 hover:scale-105"
        >
          <ArrowLeft className="h-5 w-5 text-navy-700" />
        </Button>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {step === 'select' && renderUserTypeSelection()}
        {step === 'login' && renderLoginForm()}
        {step === 'register' && renderRegisterForm()}
      </main>
    </div>
  );
};

export default Login;